import { Button, Container, InputLabel, Typography } from "@material-ui/core";
import { makeStyles } from "@material-ui/core/styles";
import { Info } from "@material-ui/icons";
import DoneIcon from "@material-ui/icons/Done";
import { App, Workouts } from "app-interfaces";
import React from "react";
import { useTranslation } from "react-i18next";

// Child Components
import { reorderItems, useStateValue } from "../../config/AppContext";
import CatalogClass from "../../models/Catalog";
// Modals
import { ConfirmModal as Delete } from "../Modals";
import DragDrop from "../Utilities/DragDrop";
import ExerciseSets from "./ExerciseSets";
import ExerciseSetsList from "./ExerciseSetsList";

const useStyles = makeStyles((theme) => ({
  container: {
    "& .MuiInputLabel-root, & .MuiTypography-h5": {
      marginBottom: 15,
      fontSize: 17,
      fontWeight: 600,
      color: "#525252",
    },
    "& .MuiTypography-h5": {
      fontSize: 16,
      // marginTop: 30,
      // marginBottom: 30,
      "& button": {
        float: "right",
        padding: 6,
        marginTop: -12,
        fontSize: 16,
        fontWeight: 500,
        color: "#ffffff",
        [theme.breakpoints.down("md")]: {
          width: 125,
          fontSize: 14,
        },
        "&.active": {
          backgroundColor: "var(--danger)",
        },
      },
    },
    "& .MuiInputBase-root": {
      height: 51,
      marginBottom: 15,
      padding: 20,
      fontSize: 16,
      "& .MuiOutlinedInput-notchedOutline": {
        border: "1px solid #dcdcdc",
      },
    },
    "& .no-sets-added": {
      display: "flex",
      flexDirection: "column",
      margin: "auto",
      padding: "60px 80px",
      height: "30vh",
      justifyContent: "center",
      textAlign: "center",
      // textTransform: 'capitalize',
      fontSize: 16,
      fontWeight: 500,
      color: "#595959",
      "& div": {
        width: "100%",
        "& svg": {
          height: 32,
          width: 32,
          fill: "#333333",
        },
      },
    },
    "& input": {
      width: "100%",
      border: "1px solid #dcdcdc",
      padding: "15px",
      fontSize: "16px",
      borderRadius: "3px",
    },
    "& textarea": {
      borderRadius: 3,
      padding: 15,
      width: "100%",
      height: 83,
      border: "1px solid #dcdcdc",
      backgroundColor: "transparent",
      fontSize: 16,
    },
    "& button": {
      height: 45,
      width: 135,
      borderRadius: 4,
      padding: "6px 12px",
      backgroundColor: "#000000",
      fontSize: 16,
      fontWeight: 500,
      textTransform: "none",
      color: "#ffffff",
      "&:hover": { backgroundColor: "#000000" },
      '&[type="submit"]': {
        backgroundColor: "var(--text-primary)",
        width: "100%",
        "&:hover": { backgroundColor: "var(--text-primary)" },
      },
    },
  },
  "& .session-workout-sets": {
    padding: "0 30px",
    "& .MuiContainer-root": { cursor: "pointer" },
    "& .drag": {
      "& .MuiContainer-root": { cursor: "move" },
    },
    "& button": {
      display: "inline-block",
      marginLeft: 15,
      textTransform: "none",
    },
  },

  spaceBtw: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
  },
  editText: {
    color: "var(--text-primary)",
    cursor: "pointer",
    // marginBottom:35
  },
  saveBtn: {
    border: "none",
    color: "var(--text-primary)",
    backgroundColor: "inherit",
  },
  activeToggle: {
    backgroundColor: "var(--danger)",
  },
}));

type ActionProps = {
  type: string;
  payload?: any;
};

const WorkoutsEditComponent = (props: any) => {
  const classes = useStyles();
  const { t } = useTranslation();
  const { catalog, Methods } = useStateValue() as {
    catalog: CatalogClass;
    Methods: App.Methods;
  };
  const { id } = props.match.params;
  const catalogWorkout = catalog.Workouts.find((el) => el.id === id);

  // Callbacks

  // Effects

  if (!id) return <></>;

  if (!catalogWorkout)
    return (
      <Container id="workouts-edit" className={classes.container}>
        {t("No matching workout with id")} {id}
      </Container>
    );

  const handleSubmit = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const formData = new FormData(e.currentTarget);
    let params = {};
    formData.forEach((value, name) => {
      if (name === "exercise_super_set") return;
      return Object.assign(params, { [name]: value });
    });
    Object.assign(params, {
      id: catalogWorkout.id,
      exercise_super_set: catalogWorkout.exercise_super_set,
      is_deleted: catalogWorkout.is_deleted,
    });
    if (window.innerWidth > 700 && !props.loading) {
      props.setLoading(true);
      props.setEditWorkout(null);
      props.setShowWorkout(null);
      return Methods.updateWorkouts(params).then(() => {
        props.setLoading(false);
        props.setShowWorkout(props.showWorkout);
      });
    } else if (window.innerWidth <= 700) {
      return Methods.updateWorkouts(params).then(() => {
        props.history.goBack();
      });
    }
  };

  return (
    <Container id="workouts-edit" className={classes.container}>
      <form onSubmit={handleSubmit}>
        {/* Name */}
        <InputLabel htmlFor="name">{t("Workout name")}</InputLabel>
        <input
          // fullWidth
          required
          id="name"
          type="text"
          name="name"
          // variant='outlined'
          placeholder={t("Workout name")}
          defaultValue={catalogWorkout.name}
          onInvalid={(e) => {
            e.currentTarget.setCustomValidity(`${t("This field is required")}`);
          }}
          onInput={(e) => {
            e.currentTarget.setCustomValidity(``);
          }}
        />
        {/* Duration */}
        <InputLabel htmlFor="duration">
          {t("Duration")} ({t("Minutes")})
        </InputLabel>
        <input
          // fullWidth
          required
          id="duration"
          name="duration"
          type="number"
          // variant='outlined'
          placeholder={t("Duration")}
          defaultValue={catalogWorkout.duration}
          onInvalid={(e) => {
            e.currentTarget.setCustomValidity(`${t("This field is required")}`);
          }}
          onInput={(e) => {
            e.currentTarget.setCustomValidity(``);
          }}
          min={5}
          max={120}
          step={5}
        />
        <InputLabel htmlFor="description">{t("Workout description")}</InputLabel>
        <textarea
          id="description"
          name="description"
          placeholder={t("Workout description")}
          defaultValue={catalogWorkout.description}
          required
          onInvalid={(e) => {
            e.currentTarget.setCustomValidity(`${t("This field is required")}`);
          }}
          onInput={(e) => {
            e.currentTarget.setCustomValidity(``);
          }}
        />
        {/* Notes */}
        <InputLabel htmlFor="notes">{t("Workout notes")}</InputLabel>
        <textarea
          id="notes"
          name="notes"
          placeholder={t("Workout notes")}
          defaultValue={catalogWorkout.notes}
        />
        <Button variant="contained" type="submit">
          {t("Update")}
        </Button>
      </form>
    </Container>
  );
};

export const EditSup = (props: any) => {
  const classes = useStyles();
  const { t } = useTranslation();
  const { catalog, Methods } = useStateValue() as {
    catalog: CatalogClass;
    Methods: App.Methods;
  };
  const { id, dispatch } = props;
  const catalogWorkout = catalog.Workouts.find((el) => el.id === id);

  const templateWorkout = {
    id: "",
    name: "",
    description: "",
    duration: 0,
    is_deleted: false,
    exercise_super_set: [] as Workouts.IExerciseSuperSet[],
  };
  const initialWorkout = catalogWorkout ? catalogWorkout : templateWorkout;

  const [superset, setSuperSet] = React.useState<Workouts.IExerciseSuperSet | null>(null);
  const [toggled, setToggled] = React.useState(false);
  const [toggleDelete, setToggleDelete] = React.useState(false);
  const [updatedWorkout, setUpdatedWorkout]: any = React.useState(null);
  const [stateWorkout, setStateWorkout] = React.useReducer(workoutReducer, initialWorkout);
  const [showWorkout, setShowWorkout] = React.useState<string | null>(null);
  const exerciseSuperSet = catalogWorkout?.exercise_super_set;

  const delSuperSetList = React.useCallback(() => {
    if (!superset || !exerciseSuperSet) return;
    const superSetIndex = exerciseSuperSet.findIndex((el) => el.id === superset.id);
    exerciseSuperSet.splice(superSetIndex, 1);
    return setSuperSet(null);
  }, [superset, exerciseSuperSet]);

  React.useEffect(() => {
    if (!superset) return setToggleDelete(false);

    delSuperSetList();
  }, [delSuperSetList, superset]);

  if (!id) return <></>;

  if (!catalogWorkout)
    return (
      <Container id="workouts-edit" className={classes.container}>
        {t("No matching workout with id")} {id}
      </Container>
    );

  function workoutReducer(workout: Workouts.IWorkout, action: ActionProps) {
    switch (action.type) {
      case "cancel": {
        return initialWorkout;
      }
      case "reorder":
        const { supersetId, targetId } = action.payload as {
          supersetId: number;
          targetId: number;
        };
        let workoutClone = { ...workout };
        workoutClone.exercise_super_set = reorderItems(
          supersetId,
          targetId,
          workoutClone.exercise_super_set
        );
        setUpdatedWorkout(workoutClone);
        // handleSubmit(workoutClone);
        return workoutClone;
      default:
        throw new Error(t(`${action.type} is not a valid option`));
    }
  }

  const generateSuperSetList = (
    accum: JSX.Element[],
    superset: Workouts.IExerciseSuperSet,
    supersetIndex: number
  ) => {
    const exerciseList = superset.exercises.map(
      (exercise, exerciseIndex): JSX.Element => (
        <ExerciseSets key={exerciseIndex} exercise={exercise} />
      )
    );

    accum = [
      ...accum,
      toggled ? (
        <DragDrop key={supersetIndex} id={`superset-${supersetIndex}`} dispatch={setStateWorkout}>
          <ExerciseSetsList
            toggled={toggled}
            key={supersetIndex}
            superset={superset}
            supersetIndex={supersetIndex}
            exerciseList={exerciseList}
            handleSuperSet={handleSuperSet}
          />
        </DragDrop>
      ) : (
        <ExerciseSetsList
          key={supersetIndex}
          superset={superset}
          supersetIndex={supersetIndex}
          exerciseList={exerciseList}
          handleSuperSet={handleSuperSet}
        />
      ),
    ];
    return accum;
  };

  const generateSuperSetSection = () =>
    stateWorkout.exercise_super_set.length > 0
      ? stateWorkout.exercise_super_set.reduce(generateSuperSetList, [])
      : t("No exercises added yet");

  const handleSuperSet = (e: React.MouseEvent<HTMLLIElement>) => {
    e.preventDefault();
    const { id } = e.currentTarget;
    const targetSuperSet = catalogWorkout.exercise_super_set.find((el) => el.id === id);
    if (targetSuperSet) return setSuperSet(targetSuperSet);
    return setSuperSet(null);
  };

  const handleSubmit = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const formData = new FormData(e.currentTarget);
    let params = {};
    formData.forEach((value, name) => {
      if (name === "exercise_super_set") return;
      return Object.assign(params, { [name]: value });
    });
    Object.assign(params, {
      id: catalogWorkout.id,
      exercise_super_set: catalogWorkout.exercise_super_set,
      is_deleted: catalogWorkout.is_deleted,
    });
    if (window.innerWidth > 700) {
      setShowWorkout(null);
      return Methods.updateWorkouts(params).then(() => {
        // props.setLoading(false);
        setShowWorkout(showWorkout);
      });
    } else if (window.innerWidth <= 700) {
      return Methods.updateWorkouts(params).then(() => {
        props.history.goBack();
      });
    }
  };
  const handleDone = () => {
    toggled &&
      updatedWorkout &&
      catalogWorkout.exercise_super_set.length > 0 &&
      dispatch({
        type: "add",
        payload: updatedWorkout,
      });
    !toggled &&
      !updatedWorkout &&
      catalogWorkout &&
      dispatch({
        type: "add",
        payload: catalogWorkout,
      });
    props.editSup[1](false);
  };

  const handleToggleCancel = () => {
    setToggled(!toggled);
    if (toggled) {
      setStateWorkout({
        type: "cancel",
      });
    }
  };

  return (
    <Container id="workouts-edit" className={classes.container}>
      <form onSubmit={handleSubmit}>
        <div className={classes.spaceBtw}>
          <Typography variant="h5">{t("Edit Workout Info")}</Typography>
          <span
            className={`${classes.saveBtn} ${classes.editText}`} //disabled={loading}
            onClick={handleDone}
          >
            {" "}
            <DoneIcon fontSize="small" /> {t("Done")}{" "}
          </span>
        </div>
        {catalogWorkout.exercise_super_set.length > 0 && (
          <Button
            variant="contained"
            style={{
              width: "fit-content",
              marginBottom: 15,
              backgroundColor: toggled ? "var(--danger)" : "#000000",
            }}
            onClick={handleToggleCancel}
          >
            {toggled ? t("Cancel Reorder") : t("Toggle Reorder")}
          </Button>
        )}
        <input
          id="exercise_super_set"
          name="exercise_super_set"
          type="text"
          style={{ display: "none" }}
          defaultValue={JSON.stringify(catalogWorkout.exercise_super_set)}
        />
        {catalogWorkout.exercise_super_set.length > 0 ? (
          <ul className="session-workout-sets" style={{ padding: "15px 0" }}>
            {generateSuperSetSection()}
          </ul>
        ) : (
          <div className="no-sets-added">
            <div>
              <Info />
            </div>
            {t("No exercises added yet")}
          </div>
        )}
      </form>
      <Delete
        show={toggleDelete}
        onSubmit={delSuperSetList}
        onHide={() => setSuperSet(null)}
        message={t("Are you sure you want to remove this exercise?")}
        title={t("Delete")}
      />
    </Container>
  );
};

export default WorkoutsEditComponent;
